import * as retry from "async-retry";
import { newStorage } from "firebaseConfig";
import { textIncludes } from "utils/table";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { getHighestRankingContactsBySearch, type RealEstateContactWithRank } from "../filters";
import { getFileFormat } from "utils/getFileFormat";
import { PATH_PUBLIC } from "routes/paths";
import { getFileExtension } from "utils/listings/display";
import { getFavoritesListingsQuery } from "utils/Haim/firestoreUtils";
import { pickMinisitePage } from "utils/minisite/minisitePage";
import {
  IMAGE_EXTENSIONS,
  VIDEO_EXTENSIONS,
  CUSTOM_BULK_MESSAGE_CONTACT_TEMPLATE_KEYS,
  initialActiveTemplates,
  type FieldTemplate,
  type ImageExtension,
  type VideoExtension,
  type PlaceholderTemplateForm,
  type AllBulkMessageTemplateKeys,
  type BulkMessageListingTemplateKey,
  type BulkMessageRecipient,
  type CustomBulkMessageContactTemplateKey
} from "./new";
import type { ProcessedListingData } from "../../../@types/sharedSchema";

export type CustomContactTemplateValues = Partial<
  Record<CustomBulkMessageContactTemplateKey, string>
>;

export function processMessageTemplate(
  message: string,
  contact: BulkMessageRecipient | null,
  listing: ProcessedListingData | null,
  placeholders: PlaceholderTemplateForm,
  generatedListingLink?: string,
  customContactValues?: CustomContactTemplateValues,
  perContactData?: Record<string, string>
): string {
  let processedMessage = message;

  if (contact) {
    let fieldTemplates: FieldTemplate[] = Object.values(initialActiveTemplates);

    for (const { id, type } of fieldTemplates) {
      let templateValue: string = "";

      const isCustomContactKey = CUSTOM_BULK_MESSAGE_CONTACT_TEMPLATE_KEYS.includes(
        id as CustomBulkMessageContactTemplateKey
      );

      if (isCustomContactKey) {
        templateValue = customContactValues?.[id as CustomBulkMessageContactTemplateKey] ?? "";
      } else if (type === "contact") {
        templateValue = contact[id] || placeholders[id];
      } else {
        templateValue = getDataFromListingFieldKey(
          id as BulkMessageListingTemplateKey,
          listing,
          generatedListingLink
        );
      }

      processedMessage = processedMessage.replaceAll(
        getFormattedTemplateStringFromFieldKey(id),
        templateValue ?? ""
      );
    }
  }

  // Process custom per-contact fields (caller-provided data)
  if (perContactData) {
    for (const [key, value] of Object.entries(perContactData)) {
      processedMessage = processedMessage.replaceAll(`{{${key}}}`, value ?? "");
    }
  }

  return processedMessage;
}

export function getDataFromListingFieldKey(
  key: string,
  listing: ProcessedListingData,
  generatedListingLink?: string
): string {
  if (!listing) return "";

  let templateValue: string = "";

  const isAddressComponentKey = ["street", "neighborhood", "city"].includes(key);

  if (isAddressComponentKey) {
    templateValue = listing.address?.[key] || "";
  } else if (key === "listingLink") {
    templateValue =
      generatedListingLink ??
      `${window.location.origin}/${PATH_PUBLIC.myListings}/favorite-id/listing-id`;
  } else {
    templateValue = listing[key] || "";
  }

  return templateValue;
}

export function getFilteredSelectedContactsByFilterInput(
  selectedContacts: BulkMessageRecipient[],
  filterInput: string
): BulkMessageRecipient[] {
  const searchWords = filterInput.trim().toLowerCase().split(" ").filter(Boolean);

  if (!searchWords.length) return selectedContacts;

  // The ranking helper is typed for whole contacts and reads only `_ranking`, which is
  // written just below; a recipient ranks the same way.
  let filteredContacts = (selectedContacts as RealEstateContactWithRank[]).filter(
    (contact: RealEstateContactWithRank) => {
      // The document id IS the phone — search it, not the legacy mirror.
      const { callerFirstName, callerLastName, id: contactPhone } = contact;

      let ranking = 0;

      let isMatchingCallerFirstName = false;
      let isMatchingCallerLastName = false;

      for (const searchWord of searchWords) {
        const isFirstNameMatch = textIncludes(callerFirstName, searchWord);
        const isLastNameMatch = textIncludes(callerLastName, searchWord);

        const isMatchPartOfName = isFirstNameMatch || isLastNameMatch;

        if (isMatchPartOfName) ranking++;

        if (isFirstNameMatch) isMatchingCallerFirstName = true;
        if (isLastNameMatch) isMatchingCallerLastName = true;
      }

      contact._ranking = ranking;

      let isMatchingCallerName = isMatchingCallerFirstName || isMatchingCallerLastName;

      if (isMatchingCallerName) return true;

      for (const searchWord of searchWords) {
        const parsedSearchWord = searchWord.replace(/^0(\d{2})/, "$1");
        const isPhoneNumberMatch = textIncludes(contactPhone, parsedSearchWord);

        if (isPhoneNumberMatch) return true;
      }

      return false;
    }
  );

  const highestRankingContacts = getHighestRankingContactsBySearch(filteredContacts);

  if (highestRankingContacts.length) {
    filteredContacts = highestRankingContacts;
  }

  return filteredContacts;
}

export async function uploadFileToFirebaseStorage(email: string, file: File): Promise<string> {
  const day = new Date().toISOString().split("T")[0];

  const filePath = `whatsapp-media/${email}/${day}/${file.name}`;

  const storageRef = ref(newStorage, filePath);

  await uploadBytes(storageRef, file);

  const src = await getDownloadURL(storageRef);

  return src;
}

export async function fetchMinisiteLinksForContacts(
  email: string,
  contactIds: string[]
): Promise<Record<string, string>> {
  const results: Record<string, string> = {};

  const promises = contactIds.map(async (contactId) => {
    try {
      // Every page of the buyer, not `limit(1)`: the link is the page they were sent (`pickMinisitePage`).
      const snapshot = await retry(
        async () => getFavoritesListingsQuery(email, contactId).get(),
        { retries: 3 }
      );

      if (snapshot.empty) {
        results[contactId] = "";
      } else {
        const docId = (pickMinisitePage(snapshot.docs) ?? snapshot.docs[0]).id;
        results[contactId] = `${window.location.origin}${PATH_PUBLIC.myListings}/${docId}`;
      }
    } catch {
      results[contactId] = "";
    }
  });

  await Promise.all(promises);

  return results;
}

export function getFormattedTemplateStringFromFieldKey(key: AllBulkMessageTemplateKeys): string {
  return `{{${key}}}`;
}

export function isDocumentFormat(fileName: string): boolean {
  const documentFormats = ["pdf", "word", "excel", "photoshop", "powerpoint", "illustrator"];

  return documentFormats.includes(getFileFormat(fileName));
}

export function isImageFormat(fileName: string): boolean {
  return IMAGE_EXTENSIONS.includes(getFileExtension(fileName, 4).toLowerCase() as ImageExtension);
}

export function isVideoFormat(fileName: string): boolean {
  return VIDEO_EXTENSIONS.includes(getFileExtension(fileName, 4).toLowerCase() as VideoExtension);
}
